import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useCallback, useId, useState } from 'react';
import { useClickOutside } from 'src/tef/components/hooks/useClickOutside';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';

interface DatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  minDate?: Date;
  maxDate?: Date;
  variant?: {
    size?: 'sm' | 'md' | 'lg';
  };
  className?: string;
  id?: string;
  name?: string;
}

const sizeStyles = {
  sm: 'py-1.5 px-3 text-sm',
  md: 'py-2 px-4 text-base',
  lg: 'py-3 px-5 text-lg',
};

export default function DatePicker({
  value,
  onChange,
  placeholder = 'Select date...',
  disabled = false,
  error = false,
  minDate,
  maxDate,
  variant = {},
  className,
  id,
  name,
}: DatePickerProps) {
  const tef = useTEF();
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(value || new Date());
  const size = variant.size || 'md';
  const uniqueId = useId();
  const pickerId = id || uniqueId;

  const handleClose = useCallback(() => setIsOpen(false), []);
  const containerRef = useClickOutside<HTMLDivElement>(handleClose, isOpen);

  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const firstDayOfWeek = monthStart.getDay();
  const paddingDays = Array(firstDayOfWeek).fill(null);

  const isDateDisabled = useCallback(
    (date: Date) => {
      if (minDate && date < minDate) return true;
      if (maxDate && date > maxDate) return true;
      return false;
    },
    [minDate, maxDate]
  );

  const handleDateSelect = useCallback(
    (date: Date) => {
      if (!isDateDisabled(date)) {
        onChange(date);
        setIsOpen(false);
      }
    },
    [isDateDisabled, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!disabled) setIsOpen(!isOpen);
      }
    },
    [disabled, isOpen]
  );

  return (
    <section ref={containerRef} className={cn('relative', className)}>
      <input type="hidden" name={name} value={value ? value.toISOString() : ''} />
      <button
        type="button"
        id={pickerId}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={value ? `Selected date: ${format(value, 'MMMM d, yyyy')}` : placeholder}
        className={cn(
          'w-full flex items-center justify-between gap-2',
          'bg-white dark:bg-slate-800',
          'border rounded-lg',
          'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
          'transition-colors',
          error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
            : 'border-slate-200 dark:border-slate-700',
          disabled && 'opacity-50 cursor-not-allowed',
          sizeStyles[size]
        )}
      >
        <span className={value ? tef.colors.foreground : tef.colors.foregroundTertiary}>
          {value ? format(value, 'MMM d, yyyy') : placeholder}
        </span>
        <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" aria-hidden="true" />
      </button>

      {isOpen && (
        <article
          className={cn(
            'absolute z-[60] mt-2',
            'bg-white dark:bg-slate-800',
            'border border-slate-200 dark:border-slate-700',
            'rounded-lg shadow-lg',
            'p-3 w-[280px]'
          )}
        >
          <header className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setViewDate(subMonths(viewDate, 1))}
              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className={cn('font-medium', tef.colors.foreground)}>{format(viewDate, 'MMMM yyyy')}</span>
            <button
              type="button"
              onClick={() => setViewDate(addMonths(viewDate, 1))}
              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </header>

          <section className="grid grid-cols-7 gap-1 mb-2">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
              <span key={day} className={cn('text-center text-xs font-medium', tef.colors.foregroundTertiary)}>
                {day}
              </span>
            ))}
          </section>

          <section className="grid grid-cols-7 gap-1" role="grid">
            {paddingDays.map((_, index) => (
              <span key={`pad-${index}`} role="gridcell" />
            ))}
            {days.map((day) => {
              const isSelected = value && isSameDay(day, value);
              const isDisabled = isDateDisabled(day);
              const isCurrentMonth = isSameMonth(day, viewDate);
              const isTodayDate = isToday(day);

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  role="gridcell"
                  onClick={() => handleDateSelect(day)}
                  disabled={isDisabled}
                  aria-selected={isSelected || undefined}
                  aria-label={format(day, 'EEEE, MMMM d, yyyy')}
                  tabIndex={isSelected ? 0 : -1}
                  className={cn(
                    'w-8 h-8 rounded-full',
                    'text-sm',
                    'transition-colors',
                    'focus:outline-none focus:ring-2 focus:ring-primary/50',
                    isSelected && 'bg-primary text-white',
                    !isSelected && isTodayDate && 'ring-1 ring-primary',
                    !isSelected && !isDisabled && 'hover:bg-slate-100 dark:hover:bg-slate-700',
                    !isSelected && isCurrentMonth && tef.colors.foreground,
                    !isSelected && !isCurrentMonth && tef.colors.foregroundTertiary,
                    isDisabled && 'opacity-30 cursor-not-allowed'
                  )}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </section>
        </article>
      )}
    </section>
  );
}

export { DatePicker };
